import is from '@sindresorhus/is';

import { apiErrorBodySchema } from '~/shared/data-contract/error';
import { is404Error } from '~/shared/error-code/error-code';
import { HTTPError } from '~/shared/http-fetcher/http-error';
import { HTTPErrorWithResponseBody } from '~/shared/http-fetcher/http-error-with-response-body';
import type { TranslationKey } from '~/shared/internationalization/translation';

export type PositionErrorMessages = Readonly<Partial<Record<string, TranslationKey>>>;

export const JOB_ARCHITECTURE_NOT_CONNECTED_ERROR_CODE = 'POS0040';

const ACCESS_GRANT_REFERENCE_ERROR_CODE = 'POS0042';
const ACCESS_COVERAGE_CHANGED_ERROR_CODE = 'POS0045';
const ARCHITECTURE_NOT_FOUND_ERROR_CODE = 'POS0001';
const DEFAULT_ARCHITECTURE_DELETION_ERROR_CODE = 'POS0048';
const MANAGER_CHANGE_RUNNING_ERROR_CODE = 'POS0049';
const MANAGER_CHANGE_NOT_RETRYABLE_ERROR_CODE = 'POS0050';

const NOT_FOUND_STATUS = 404;
const CONFLICT_STATUS = 409;
const TOO_MANY_REQUESTS_STATUS = 429;

const POSITION_ERROR_TRANSLATION_KEYS: PositionErrorMessages = {
    [ARCHITECTURE_NOT_FOUND_ERROR_CODE]: 'pages.position-management.errors.item-not-found',
    POS0002: 'pages.position-management.errors.position-not-found',
    POS0003: 'pages.position-management.errors.version-not-found',
    POS0004: 'pages.position-management.errors.item-not-found',
    POS0005: 'pages.position-management.errors.item-not-found',
    POS0006: 'pages.position-management.errors.item-not-found',
    POS0007: 'pages.position-management.errors.stale-version',
    POS0008: 'pages.position-management.errors.position-archived',
    POS0009: 'pages.position-management.errors.concurrent-change',
    POS0010: 'pages.position-management.errors.attribute-not-editable',
    POS0011: 'pages.position-management.errors.attribute-not-editable',
    POS0012: 'pages.position-management.errors.attribute-key-in-use',
    POS0013: 'pages.position-management.errors.invalid-value',
    POS0014: 'pages.position-management.errors.invalid-reporting-line',
    POS0015: 'pages.position-management.errors.not-a-level',
    POS0016: 'pages.position-management.errors.chart-view-invalid',
    POS0017: 'pages.position-management.errors.review-denied',
    POS0018: 'pages.position-management.errors.reviewer-invalid',
    POS0019: 'pages.position-management.errors.translation-retry-invalid',
    POS0021: 'pages.position-management.errors.attribute-in-use',
    POS0022: 'pages.position-management.errors.external-id-in-use',
    POS0023: 'pages.position-management.errors.option-in-use',
    [JOB_ARCHITECTURE_NOT_CONNECTED_ERROR_CODE]: 'pages.position-management.connected-job-architecture.gate',
    [ACCESS_GRANT_REFERENCE_ERROR_CODE]: 'pages.position-management.errors.access-grant-reference',
    POS0043: 'pages.position-management.errors.outside-access-area',
    POS0044: 'pages.position-management.errors.access-grant-duplicate',
    [ACCESS_COVERAGE_CHANGED_ERROR_CODE]: 'pages.position-management.errors.access-coverage-changed',
    POS0046: 'pages.position-management.errors.access-grant-target-invalid',
    POS0047: 'pages.position-management.errors.access-grant-invalid',
    [DEFAULT_ARCHITECTURE_DELETION_ERROR_CODE]: 'pages.position-management.errors.architecture-is-default',
    [MANAGER_CHANGE_RUNNING_ERROR_CODE]: 'pages.position-management.manager-change.running',
    [MANAGER_CHANGE_NOT_RETRYABLE_ERROR_CODE]: 'pages.position-management.manager-change.not-retryable',
    POS0051: 'pages.position-management.errors.manager-outside-area',
};

export const isPositionClientError = (error: unknown): boolean =>
    error instanceof HTTPError &&
    error.response.status >= 400 &&
    error.response.status < 500 &&
    error.response.status !== TOO_MANY_REQUESTS_STATUS;

const NOT_FOUND_ERROR_CODES: ReadonlySet<string> = new Set(['POS0002', 'POS0003']);

const OUTDATED_POSITION_ERROR_CODES: ReadonlySet<string> = new Set([
    'POS0007',
    'POS0008',
    'POS0009',
    'POS0017',
    'POS0019',
]);

export const positionErrorCode = (error: unknown): string =>
    is.object(error) && 'errorCode' in error && is.string(error.errorCode) ? error.errorCode : '';

export const isPositionNotFoundError = (error: unknown): boolean =>
    NOT_FOUND_ERROR_CODES.has(positionErrorCode(error)) ||
    is404Error(error) ||
    (error instanceof HTTPError && error.response.status === NOT_FOUND_STATUS);

const STALE_JOB_ARCHITECTURE_CONNECTION_ERROR_CODES: ReadonlySet<string> = new Set([
    JOB_ARCHITECTURE_NOT_CONNECTED_ERROR_CODE,
    'POS0015',
]);

export const isStaleJobArchitectureConnectionError = (error: unknown): boolean =>
    STALE_JOB_ARCHITECTURE_CONNECTION_ERROR_CODES.has(positionErrorCode(error));

export const isOutdatedPositionError = (error: unknown): boolean =>
    OUTDATED_POSITION_ERROR_CODES.has(positionErrorCode(error));

const ACCESS_LOSS_ERROR_CODES: ReadonlySet<string> = new Set(['AUT0002', 'POS0002']);

export const isPositionAccessLossError = (error: unknown): boolean =>
    ACCESS_LOSS_ERROR_CODES.has(positionErrorCode(error));

export const isAccessCoverageChangedError = (error: unknown): boolean =>
    positionErrorCode(error) === ACCESS_COVERAGE_CHANGED_ERROR_CODE;

const MANAGER_CHANGE_CONFLICT_ERROR_CODES: ReadonlySet<string> = new Set([
    MANAGER_CHANGE_RUNNING_ERROR_CODE,
    MANAGER_CHANGE_NOT_RETRYABLE_ERROR_CODE,
]);

export const isManagerChangeConflictError = (error: unknown): boolean =>
    MANAGER_CHANGE_CONFLICT_ERROR_CODES.has(positionErrorCode(error));

const STALE_ARCHITECTURE_ERROR_CODES: ReadonlySet<string> = new Set([
    ARCHITECTURE_NOT_FOUND_ERROR_CODE,
    DEFAULT_ARCHITECTURE_DELETION_ERROR_CODE,
]);

export const isStaleArchitectureError = (error: unknown): boolean =>
    STALE_ARCHITECTURE_ERROR_CODES.has(positionErrorCode(error));

export const positionErrorDetail = (error: unknown): string | undefined => {
    if (
        positionErrorCode(error) !== ACCESS_GRANT_REFERENCE_ERROR_CODE ||
        !(error instanceof HTTPErrorWithResponseBody)
    ) {
        return undefined;
    }
    const body = apiErrorBodySchema.safeParse(error.body);

    return body.success ? body.data.errors[0]?.message : undefined;
};

export const positionErrorTranslationKey = (
    error: unknown,
    contextMessages: PositionErrorMessages = {},
): TranslationKey => {
    const errorCode = positionErrorCode(error);
    const fallbackKey = isPositionClientError(error)
        ? 'pages.position-management.errors.rejected'
        : 'pages.position-management.errors.generic';

    return contextMessages[errorCode] ?? POSITION_ERROR_TRANSLATION_KEYS[errorCode] ?? fallbackKey;
};

export const connectJobArchitectureErrorTranslationKey = (error: unknown): TranslationKey =>
    error instanceof HTTPError && error.response.status === CONFLICT_STATUS
        ? 'pages.position-management.connected-job-architecture.run-in-progress'
        : positionErrorTranslationKey(error);
